import './featured-paper.scss'

import { graphql } from 'gatsby'
import React from 'react'

import Button from '@/components/button/button'
import Image from '@/components/image/image'
import Link from '@/components/link/link'
import TitleLink from '@/components/title-link/title-link'
import { getModelMessages } from '@/utils/get-model-messages'
import { generateUrlFromNode } from '@/utils/url'

const FeaturedPaper: React.FC<
  Queries.SectionFeaturedPaperFragment & {
    moreInfo: BlockFeaturedMultipleContent
    nodes: (Highlights | Card | null)[]
  }
> = ({ featuredPaper, title, originalId, moreInfo, nodes }) => {
  const papers = nodes?.filter((node) =>
    Boolean(node?.id && moreInfo?.blockNodeIds?.includes(node.id))
  )
  if (!featuredPaper) return <></>
  const modelMessages = getModelMessages(featuredPaper?.__typename)
  const url = generateUrlFromNode(featuredPaper)
  return (
    <div className="featured-paper">
      <div className="container">
        <div className="row">
          <div className="col">
            <h2>{title}</h2>
          </div>
        </div>
        <div className="row">
          <div className="col-md-7 col-xl-4 order-2 order-md-1 mb-4 mb-md-4">
            {featuredPaper?.topic && (
              <div className="topic">
                <Button
                  to={generateUrlFromNode(featuredPaper?.topic)}
                  title={featuredPaper.topic.externalName}
                  color="tertiary"
                />
              </div>
            )}

            <TitleLink
              to={url}
              title={featuredPaper.externalName ?? ''}
              htmltag="h3"
            />
            <div className="description">{featuredPaper.excerpt}</div>
            <div className="authors">
              {featuredPaper.authors && featuredPaper.authors.length > 0 && (
                <>
                  <h4>Authors /</h4>
                  {featuredPaper.authors.map((item, index) => (
                    <div key={`author-${index}`} className="name">
                      {item?.person?.firstName} {item?.person?.secondName}
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="links">
              <Button to={url} title={modelMessages.button} />
              {modelMessages.buttonAllUrl && (
                <Button
                  to={modelMessages.buttonAllUrl}
                  title={modelMessages.buttonAll}
                  color="secondary"
                />
              )}
            </div>
          </div>
          <div className="col-md-5 col-xl-5 order-md-2 order-lg-1 order-lg-2">
            <Link to={url}>
              <Image
                image={featuredPaper?.cover?.gatsbyImageData}
                alt={featuredPaper.externalName ?? ''}
              />
            </Link>
          </div>
          <div className="col-md-12 col-xl-3 order-3 order-xl-3">
            <div className="related-list">
              {papers
                ?.filter((paper) => paper?.originalId !== originalId)
                .slice(0, 3)
                .map((item) => {
                  const urlRelated = generateUrlFromNode(item)
                  return (
                    <div className="item" key={`paper-${item?.slug}`}>
                      {item?.topic && (
                        <div className="topic">
                          <Button
                            to={generateUrlFromNode(item?.topic)}
                            title={item?.topic?.externalName}
                            color="tertiary"
                          />
                        </div>
                      )}

                      <TitleLink
                        to={urlRelated}
                        title={item?.externalName ?? ''}
                        htmltag="h3"
                      />
                      {item?.excerpt && <p>{item?.excerpt}</p>}
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FeaturedPaper

export const query = graphql`
  fragment SectionFeaturedPaper on DatoCmsFeaturedPaper {
    __typename
    originalId
    id
    title
    navigationLabel
    featuredPaper {
      __typename
      originalId
      externalName
      excerpt
      slug
      topic {
        ...TopicReference
      }
      cover {
        __typename
        ...imgFeaturedPaper
      }
      authors {
        __typename
        ... on DatoCmsPctAuthor {
          person {
            __typename
            firstName
            secondName
          }
        }
      }
    }
  }
`
